import { useEffect, useMemo, useRef, useState } from "react";
import { deleteCompanyLogo, getCatalogItems, getCompanyBankCatalog, getCompanyBankLookup, getCompanyProfile, updateCompanyProfile, uploadCompanyLogo, uploadCompanyLogoCrop } from "../api";
import VestaSelect from "../components/common/VestaSelect";
import { findMunicipality, normalizePostalCodeValue, postalCodeCatalogOptions } from "../utils/customerCatalogs";
import { getUserPageLevel, readStoredUser } from "../utils/permissions";
import "../assets/css/windows.css";
import "../assets/css/company.css";

const EMPTY_COMPANY = {
  description: "",
  legalForm: "",
  vatNumber: "",
  fiscalCode: "",
  sdiCode: "",
  reaCode: "",
  businessRegistryNumber: "",
  shareCapital: "",
  shareHoldersNumber: "",
  liquidationStatus: "NOT_IN_LIQUIDATION",
  taxRegime: "",
  eoriCode: "",
  pec: "",
  email: "",
  phone: "",
  website: "",
  iban: "",
  bic: "",
  bankAccount: [],
  subdomain: "",
  dbName: "",
  dbCode: "",
  logoUrl: "",
  logoSource: "",
  logoPositionX: 50,
  logoPositionY: 50,
  logoZoom: 1,
};

const LIQUIDATION_OPTIONS = [
  { value: "NOT_IN_LIQUIDATION", label: "Non in Liquidazione" },
  { value: "IN_LIQUIDATION", label: "In Liquidazione" },
];

const LEGAL_FORM_OPTIONS = [
  { value: "SRL", label: "S.r.l. - Società a Responsabilità Limitata" },
  { value: "SRLS", label: "S.r.l.s. - Società a Responsabilità Limitata Semplificata" },
  { value: "SPA", label: "S.p.A. - Società per Azioni" },
  { value: "SAPA", label: "S.a.p.A. - Società in Accomandita per Azioni" },
  { value: "SNC", label: "S.n.c. - Società in Nome Collettivo" },
  { value: "SAS", label: "S.a.s. - Società in Accomandita Semplice" },
  { value: "SS", label: "S.s. - Società Semplice" },
  { value: "COOP", label: "Società Cooperativa" },
  { value: "CONSORZIO", label: "Consorzio" },
  { value: "DITTA_INDIVIDUALE", label: "Ditta Individuale" },
  { value: "ALTRO", label: "Altro" },
];

const TAX_REGIME_OPTIONS = [
  { value: "RF01", label: "RF01 - Ordinario" },
  { value: "RF02", label: "RF02 - Contribuenti minimi" },
  { value: "RF04", label: "RF04 - Agricoltura e attività connesse / pesca" },
  { value: "RF05", label: "RF05 - Vendita sali e tabacchi" },
  { value: "RF06", label: "RF06 - Commercio fiammiferi" },
  { value: "RF07", label: "RF07 - Editoria" },
  { value: "RF08", label: "RF08 - Gestione servizi telefonia pubblica" },
  { value: "RF09", label: "RF09 - Rivendita documenti di trasporto pubblico e sosta" },
  { value: "RF10", label: "RF10 - Intrattenimenti, giochi e altre attività" },
  { value: "RF11", label: "RF11 - Agenzie viaggi e turismo" },
  { value: "RF12", label: "RF12 - Agriturismo" },
  { value: "RF13", label: "RF13 - Vendite a domicilio" },
  { value: "RF14", label: "RF14 - Rivendita beni usati, arte, antiquariato o collezione" },
  { value: "RF15", label: "RF15 - Agenzie di vendite all'asta" },
  { value: "RF16", label: "RF16 - IVA per cassa P.A." },
  { value: "RF17", label: "RF17 - IVA per cassa altri soggetti" },
  { value: "RF18", label: "RF18 - Altro" },
  { value: "RF19", label: "RF19 - Regime forfettario" },
];

const BANK_ACCOUNT_TYPE_OPTIONS = [
  { value: "CURRENT", label: "Conto Corrente" },
  { value: "DEPOSIT", label: "Conto Deposito" },
  { value: "OTHER", label: "Altro Rapporto Bancario" },
];

const EMPTY_BANK_ACCOUNT = {
  accountType: "CURRENT",
  bankName: "",
  branchName: "",
  abi: "",
  cab: "",
  cin: "",
  accountNumber: "",
  iban: "",
  bic: "",
  siaCode: "",
  address: "",
  cap: "",
  city: "",
  province: "",
  primary: false,
};

const LOGO_CANVAS_WIDTH = 840;
const LOGO_CANVAS_HEIGHT = 528;
const LOGO_ZOOM_MIN = 1;
const LOGO_ZOOM_MAX = 5;
const LOGO_ZOOM_WHEEL_FACTOR = 0.0015;
const LOGO_WHEEL_SAVE_DELAY = 450;

function normalizeIban(value) {
  return String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function ibanRemainder(value) {
  let remainder = 0;
  for (const character of String(value || "")) {
    const chunk = /[A-Z]/.test(character) ? String(character.charCodeAt(0) - 55) : character;
    for (const digit of chunk) remainder = (remainder * 10 + Number(digit)) % 97;
  }
  return remainder;
}

function validItalianIban(value) {
  const iban = normalizeIban(value);
  if (!/^IT\d{2}[A-Z]\d{10}[A-Z0-9]{12}$/.test(iban)) return false;
  return ibanRemainder(`${iban.slice(4)}${iban.slice(0, 4)}`) === 1;
}

const ITALIAN_CIN_ODD = {
  0: 1, 1: 0, 2: 5, 3: 7, 4: 9, 5: 13, 6: 15, 7: 17, 8: 19, 9: 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18,
  N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};

function italianCin(abi, cab, accountNumber) {
  const account = String(accountNumber || "").toUpperCase().replace(/[^A-Z0-9]/g, "").padStart(12, "0").slice(-12);
  const bbanBody = `${abi}${cab}${account}`;
  if (!/^\d{10}[A-Z0-9]{12}$/.test(bbanBody)) return "";
  let total = 0;
  [...bbanBody].forEach((character, index) => {
    if ((index + 1) % 2 === 1) total += ITALIAN_CIN_ODD[character] ?? 0;
    else total += /\d/.test(character) ? Number(character) : character.charCodeAt(0) - 65;
  });
  return String.fromCharCode(65 + (total % 26));
}

function calculateItalianIban(abi, cab, accountNumber) {
  const cleanAbi = String(abi || "").replace(/\D/g, "").slice(0, 5);
  const cleanCab = String(cab || "").replace(/\D/g, "").slice(0, 5);
  const account = String(accountNumber || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
  if (!/^\d{5}$/.test(cleanAbi) || !/^\d{5}$/.test(cleanCab) || !account) return null;
  const paddedAccount = account.padStart(12, "0");
  const cin = italianCin(cleanAbi, cleanCab, paddedAccount);
  if (!cin) return null;
  const bban = `${cin}${cleanAbi}${cleanCab}${paddedAccount}`;
  const check = String(98 - ibanRemainder(`${bban}IT00`)).padStart(2, "0");
  return { cin, accountNumber: paddedAccount, iban: `IT${check}${bban}` };
}

function parseItalianIban(value) {
  const iban = normalizeIban(value);
  if (iban.length !== 27 || !iban.startsWith("IT")) return null;
  return {
    iban,
    cin: iban.slice(4, 5),
    abi: iban.slice(5, 10),
    cab: iban.slice(10, 15),
    accountNumber: iban.slice(15, 27),
  };
}

function normalizeBankAccount(row = {}) {
  return {
    ...EMPTY_BANK_ACCOUNT,
    ...row,
    accountType: String(row.accountType || "CURRENT").toUpperCase(),
    bankName: String(row.bankName || ""),
    branchName: String(row.branchName || ""),
    abi: String(row.abi || "").replace(/\D/g, "").slice(0, 5),
    cab: String(row.cab || "").replace(/\D/g, "").slice(0, 5),
    cin: String(row.cin || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 1),
    accountNumber: String(row.accountNumber || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12),
    iban: normalizeIban(row.iban),
    bic: String(row.bic || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11),
    siaCode: String(row.siaCode || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5),
    address: String(row.address || ""),
    cap: String(row.cap || "").replace(/\D/g, "").slice(0, 5),
    city: String(row.city || ""),
    province: String(row.province || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 2),
    primary: row.primary === true,
  };
}

function normalizeItem(item = {}) {
  const bankAccount = Array.isArray(item.bankAccount) ? item.bankAccount.map(normalizeBankAccount) : [];
  return {
    ...EMPTY_COMPANY,
    ...item,
    shareCapital: item.shareCapital ?? "",
    shareHoldersNumber: item.shareHoldersNumber ?? "",
    bankAccount,
  };
}

function syncTenant(tenant) {
  if (!tenant) return;
  localStorage.setItem("tenant", JSON.stringify(tenant));
  window.dispatchEvent(new CustomEvent("vesta:tenant-updated"));
}

function apiError(error, fallback) {
  return error?.response?.data?.error || fallback;
}

function validateCompanyDraft(draft = {}) {
  const errors = {};
  const description = String(draft.description || "").trim();
  const vatNumber = String(draft.vatNumber || "").trim().toUpperCase();
  const fiscalCode = String(draft.fiscalCode || "").trim().toUpperCase();
  const sdiCode = String(draft.sdiCode || "").trim().toUpperCase();
  const shareholders = draft.shareHoldersNumber === "" || draft.shareHoldersNumber == null ? 0 : Number(draft.shareHoldersNumber);

  if (!/^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/.test(String(draft.subdomain || "").trim().toLowerCase())) errors.subdomain = "Sottodominio non valido (1-63 caratteri: lettere, numeri e trattini interni)";
  if (["www", "api", "admin", "static", "cdn"].includes(String(draft.subdomain || "").trim().toLowerCase())) errors.subdomain = "Sottodominio riservato";
  if (!description) errors.description = "Ragione Sociale obbligatoria";
  else if (description.length > 180) errors.description = "Massimo 180 caratteri";
  if (vatNumber && !/^[A-Z0-9]{5,28}$/.test(vatNumber)) errors.vatNumber = "Partita IVA non valida";
  if (fiscalCode && !/^[A-Z0-9]{5,28}$/.test(fiscalCode)) errors.fiscalCode = "Codice Fiscale non valido";
  if (sdiCode && !/^[A-Z0-9]{6,7}$/.test(sdiCode)) errors.sdiCode = "Inserisci 6 o 7 caratteri alfanumerici";
  if (!Number.isInteger(shareholders) || shareholders < 0) errors.shareHoldersNumber = "Numero Soci non valido";

  const banks = Array.isArray(draft.bankAccount) ? draft.bankAccount : [];
  if (banks.filter((bank) => bank?.primary === true).length > 1) errors.bankAccount = "Può esserci una sola banca Principale";
  banks.forEach((bank, index) => {
    const key = (name) => `bankAccount.${index}.${name}`;
    if (!/^\d{5}$/.test(String(bank.abi || ""))) errors[key("abi")] = "ABI: 5 cifre obbligatorie";
    if (!/^\d{5}$/.test(String(bank.cab || ""))) errors[key("cab")] = "CAB: 5 cifre obbligatorie";
    if (!/^[A-Z0-9]{1,12}$/.test(String(bank.accountNumber || "").toUpperCase())) errors[key("accountNumber")] = "c/C obbligatorio, massimo 12 caratteri";
    if (!validItalianIban(bank.iban)) errors[key("iban")] = "IBAN italiano non valido";
    if (bank.bic && !/^[A-Z0-9]{8}([A-Z0-9]{3})?$/.test(String(bank.bic).toUpperCase())) errors[key("bic")] = "BIC / SWIFT non valido";
    if (bank.siaCode && !/^[A-Z0-9]{5}$/.test(String(bank.siaCode).toUpperCase())) errors[key("siaCode")] = "Codice SIA: 5 caratteri alfanumerici";
    const expected = calculateItalianIban(bank.abi, bank.cab, bank.accountNumber);
    if (expected && normalizeIban(bank.iban) && expected.iban !== normalizeIban(bank.iban)) errors[key("iban")] = "IBAN non coerente con ABI, CAB e c/C";
  });
  return errors;
}

function clampLogoPosition(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return 50;
  return Math.min(100, Math.max(0, number));
}

function clampLogoZoom(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return LOGO_ZOOM_MIN;
  return Math.min(LOGO_ZOOM_MAX, Math.max(LOGO_ZOOM_MIN, number));
}

function logoDrawMetrics(image, targetWidth, targetHeight, zoom = 1) {
  const safeTargetWidth = Math.max(Number(targetWidth) || 0, 1);
  const safeTargetHeight = Math.max(Number(targetHeight) || 0, 1);
  const imageWidth = Math.max(Number(image?.naturalWidth) || 0, 1);
  const imageHeight = Math.max(Number(image?.naturalHeight) || 0, 1);
  const coverScale = Math.max(safeTargetWidth / imageWidth, safeTargetHeight / imageHeight);
  const scale = coverScale * clampLogoZoom(zoom);
  const drawWidth = imageWidth * scale;
  const drawHeight = imageHeight * scale;
  return {
    drawWidth,
    drawHeight,
    overflowX: Math.max(0, drawWidth - safeTargetWidth),
    overflowY: Math.max(0, drawHeight - safeTargetHeight),
  };
}

function paintLogoCanvas(canvas, image, position, zoom = 1) {
  if (!canvas || !image) return;
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return;

  const targetWidth = canvas.width;
  const targetHeight = canvas.height;
  const metrics = logoDrawMetrics(image, targetWidth, targetHeight, zoom);
  const x = clampLogoPosition(position?.x);
  const y = clampLogoPosition(position?.y);
  const drawX = -metrics.overflowX * (x / 100);
  const drawY = -metrics.overflowY * (y / 100);

  context.fillStyle = "#ffffff";
  context.fillRect(0, 0, targetWidth, targetHeight);
  context.drawImage(image, drawX, drawY, metrics.drawWidth, metrics.drawHeight);
}

function loadLogoImage(source) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Impossibile leggere l'immagine del logo"));
    image.src = source;
  });
}

function createLogoCropBlob(image, position, zoom = 1) {
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_CANVAS_WIDTH;
  canvas.height = LOGO_CANVAS_HEIGHT;
  paintLogoCanvas(canvas, image, position, zoom);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("Impossibile creare il ritaglio del logo")), "image/png", 0.96);
  });
}

function logoBlobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error || new Error("Impossibile leggere il ritaglio del logo"));
    reader.readAsDataURL(blob);
  });
}

function readStoredTenant() {
  try {
    return JSON.parse(localStorage.getItem("tenant") || "null");
  } catch {
    return null;
  }
}

export default function Company() {
  const user = readStoredUser();
  const pageLevel = getUserPageLevel(user, "company");
  const canEdit = pageLevel >= 2;
  const canDelete = pageLevel >= 3;
  const fileInputRef = useRef(null);
  const logoCanvasRef = useRef(null);
  const logoPreviewBoxRef = useRef(null);
  const logoImageRef = useRef(null);
  const logoDragRef = useRef(null);
  const logoWheelTimerRef = useRef(null);
  const logoWheelHandlerRef = useRef(null);
  const logoSaveInFlightRef = useRef(false);
  const logoPendingSaveRef = useRef(null);
  const logoViewRef = useRef({ position: { x: 50, y: 50 }, zoom: 1 });
  const savedLogoViewRef = useRef({ position: { x: 50, y: 50 }, zoom: 1 });
  const [saved, setSaved] = useState(EMPTY_COMPANY);
  const [draft, setDraft] = useState(EMPTY_COMPANY);
  const [logoPreview, setLogoPreview] = useState("");
  const [logoPosition, setLogoPosition] = useState({ x: 50, y: 50 });
  const [logoZoom, setLogoZoom] = useState(1);
  const [logoDragging, setLogoDragging] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [validationActive, setValidationActive] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [bankLookupErrors, setBankLookupErrors] = useState({});
  const [bankCatalog, setBankCatalog] = useState([]);
  const [bankBranches, setBankBranches] = useState({});
  const [expandedBankRows, setExpandedBankRows] = useState({});
  const [municipalities, setMunicipalities] = useState([]);
  const [postalCodes, setPostalCodes] = useState([]);
  const bankLookupSequenceRef = useRef({});
  const bankBranchLoadingRef = useRef({});

  const hasProfileChanges = useMemo(() => {
    const editableKeys = [
      "subdomain", "description", "legalForm", "vatNumber", "fiscalCode", "sdiCode", "reaCode",
      "businessRegistryNumber", "shareCapital", "shareHoldersNumber", "liquidationStatus",
      "taxRegime", "eoriCode",
    ];
    if (editableKeys.some((key) => String(draft[key] ?? "") !== String(saved[key] ?? ""))) return true;
    return JSON.stringify((draft.bankAccount || []).map(normalizeBankAccount)) !== JSON.stringify((saved.bankAccount || []).map(normalizeBankAccount));
  }, [draft, saved]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const data = await getCompanyProfile();
        if (!active) return;
        const item = normalizeItem(data?.item);
        setSaved(item);
        setDraft(item);
        setLogoPreview(item.logoUrl || "");
        setLogoPosition({ x: clampLogoPosition(item.logoPositionX), y: clampLogoPosition(item.logoPositionY) });
        setLogoZoom(clampLogoZoom(item.logoZoom));
        syncTenant(data?.tenant);
      } catch (err) {
        if (active) setError(apiError(err, "Impossibile caricare l'anagrafica azienda"));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let active = true;
    getCompanyBankCatalog()
      .then((data) => {
        if (!active) return;
        setBankCatalog(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (active) setBankCatalog([]);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    Promise.all([
      getCatalogItems("municipalities", { includeInactive: true }).catch(() => ({ items: [] })),
      getCatalogItems("postal-codes", { includeInactive: true }).catch(() => ({ items: [] })),
    ]).then(([municipalityData, postalData]) => {
      if (!active) return;
      setMunicipalities(Array.isArray(municipalityData?.items) ? municipalityData.items : []);
      setPostalCodes(Array.isArray(postalData?.items) ? postalData.items : []);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const abis = [...new Set((draft.bankAccount || []).map((bank) => String(bank?.abi || "")).filter((abi) => /^\d{5}$/.test(abi)))];
    abis.forEach((abi) => { void loadBankBranches(abi); });
  }, [draft.bankAccount]);

  useEffect(() => {
    const source = draft.logoSource || draft.logoUrl || "";
    if (!source) {
      logoImageRef.current = null;
      return undefined;
    }
    let active = true;
    loadLogoImage(source).then((image) => {
      if (!active) return;
      logoImageRef.current = image;
      paintLogoCanvas(logoCanvasRef.current, image, logoPosition, logoZoom);
    }).catch(() => {
      if (active) logoImageRef.current = null;
    });
    return () => { active = false; };
  }, [draft.logoSource, draft.logoUrl]);

  useEffect(() => {
    logoViewRef.current = { position: logoPosition, zoom: logoZoom };
    paintLogoCanvas(logoCanvasRef.current, logoImageRef.current, logoPosition, logoZoom);
  }, [logoPosition, logoZoom]);

  useEffect(() => {
    savedLogoViewRef.current = {
      position: {
        x: clampLogoPosition(saved.logoPositionX),
        y: clampLogoPosition(saved.logoPositionY),
      },
      zoom: clampLogoZoom(saved.logoZoom),
    };
  }, [saved]);

  useEffect(() => () => {
    if (logoWheelTimerRef.current) clearTimeout(logoWheelTimerRef.current);
    logoPendingSaveRef.current = null;
  }, []);

  // Mantiene sempre un riferimento all'handler piu recente senza dover
  // smontare/rimontare il listener quando cambia lo stato di salvataggio.
  logoWheelHandlerRef.current = handleLogoWheel;

  useEffect(() => {
    if (loading) return undefined;
    const previewBox = logoPreviewBoxRef.current;
    if (!previewBox) return undefined;

    const onWheel = (event) => {
      // Il wheel sul logo appartiene SEMPRE al logo: la pagina non deve
      // mai ricevere lo scroll, nemmeno mentre topbar/report si aggiornano.
      event.preventDefault();
      event.stopPropagation();
      logoWheelHandlerRef.current?.(event);
    };

    previewBox.addEventListener("wheel", onWheel, { passive: false, capture: true });
    return () => previewBox.removeEventListener("wheel", onWheel, { capture: true });
  }, [loading]);

  function clearValidationKey(key) {
    setValidationErrors((current) => {
      if (!current[key]) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  function updateField(key, value) {
    if (!canEdit) return;
    setDraft((current) => ({ ...current, [key]: value }));
    clearValidationKey(key);
    setError("");
  }

  function resetProfile() {
    setDraft(saved);
    setValidationActive(false);
    setValidationErrors({});
    setBankLookupErrors({});
    setError("");
  }

  function addBankAccount() {
    if (!canEdit) return;
    setDraft((current) => {
      const rows = current.bankAccount || [];
      return { ...current, bankAccount: [...rows, { ...EMPTY_BANK_ACCOUNT, primary: rows.length === 0 }] };
    });
    setError("");
  }

  function removeBankAccount(index) {
    if (!canEdit) return;
    setDraft((current) => ({ ...current, bankAccount: (current.bankAccount || []).filter((_, rowIndex) => rowIndex !== index) }));
    setValidationErrors((current) => Object.fromEntries(Object.entries(current).filter(([key]) => !key.startsWith("bankAccount."))));
    setBankLookupErrors({});
    setExpandedBankRows({});
    setError("");
  }

  async function loadBankBranches(abi) {
    const token = String(abi || "");
    if (!/^\d{5}$/.test(token) || bankBranches[token] || bankBranchLoadingRef.current[token]) return;
    bankBranchLoadingRef.current[token] = true;
    try {
      const data = await getCompanyBankCatalog(token);
      setBankBranches((current) => ({ ...current, [token]: Array.isArray(data?.items) ? data.items : [] }));
    } catch (_error) {
      setBankBranches((current) => ({ ...current, [token]: [] }));
    } finally {
      delete bankBranchLoadingRef.current[token];
    }
  }

  function selectBankAbi(index, abi) {
    if (!canEdit) return;
    const selected = bankCatalog.find((item) => String(item.abi) === String(abi));
    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      const row = normalizeBankAccount(rows[index] || EMPTY_BANK_ACCOUNT);
      rows[index] = {
        ...row,
        abi: String(abi || ""),
        cab: "",
        bankName: selected?.bankName || "",
        branchName: "",
        address: "",
        cap: "",
        city: "",
        province: "",
        iban: "",
        cin: "",
        bic: "",
      };
      return { ...current, bankAccount: rows };
    });
    clearValidationKey(`bankAccount.${index}.abi`);
    clearValidationKey(`bankAccount.${index}.cab`);
    clearValidationKey(`bankAccount.${index}.iban`);
    setBankLookupErrors((current) => ({ ...current, [index]: "" }));
    if (/^\d{5}$/.test(String(abi || ""))) void loadBankBranches(String(abi));
  }

  function selectBankCab(index, cab) {
    if (!canEdit) return;
    const currentRow = normalizeBankAccount(draft.bankAccount?.[index] || EMPTY_BANK_ACCOUNT);
    const selected = (bankBranches[currentRow.abi] || []).find((item) => String(item.cab) === String(cab));
    let next = {
      ...currentRow,
      cab: String(cab || ""),
      bankName: selected?.bankName || currentRow.bankName || "",
      branchName: selected?.branchName || "",
      address: selected?.address || "",
      cap: selected?.cap || "",
      city: selected?.city || "",
      province: selected?.province || "",
      bic: selected?.bic || currentRow.bic || "",
    };
    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      const calculated = calculateItalianIban(next.abi, next.cab, next.accountNumber);
      rows[index] = calculated ? { ...next, iban: calculated.iban, cin: calculated.cin } : next;
      return { ...current, bankAccount: rows };
    });
    clearValidationKey(`bankAccount.${index}.cab`);
    clearValidationKey(`bankAccount.${index}.iban`);
    setBankLookupErrors((current) => ({ ...current, [index]: "" }));
  }

  async function resolveBankAccount(index, abi, cab) {
    if (!/^\d{5}$/.test(String(abi || "")) || !/^\d{5}$/.test(String(cab || ""))) return;
    const sequence = (bankLookupSequenceRef.current[index] || 0) + 1;
    bankLookupSequenceRef.current[index] = sequence;
    try {
      const data = await getCompanyBankLookup(abi, cab);
      if (bankLookupSequenceRef.current[index] !== sequence) return;
      const item = data?.item || {};
      setDraft((current) => {
        const rows = [...(current.bankAccount || [])];
        if (!rows[index] || rows[index].abi !== abi || rows[index].cab !== cab) return current;
        rows[index] = {
          ...rows[index],
          bankName: item.bankName || "",
          branchName: item.branchName || "",
          address: item.address || "",
          cap: item.cap || "",
          city: item.city || "",
          province: item.province || "",
          bic: rows[index].bic || item.bic || "",
        };
        return { ...current, bankAccount: rows };
      });
      setBankLookupErrors((current) => ({ ...current, [index]: "" }));
    } catch (err) {
      if (bankLookupSequenceRef.current[index] !== sequence) return;
      setBankLookupErrors((current) => ({ ...current, [index]: apiError(err, "ABI/CAB non presenti in sys_bank") }));
      setDraft((current) => {
        const rows = [...(current.bankAccount || [])];
        if (!rows[index]) return current;
        rows[index] = { ...rows[index], bankName: "", branchName: "", address: "", cap: "", city: "", province: "" };
        return { ...current, bankAccount: rows };
      });
    }
  }

  function updateBankField(index, key, rawValue) {
    if (!canEdit) return;
    if (key === "primary") {
      setDraft((current) => ({ ...current, bankAccount: (current.bankAccount || []).map((row, rowIndex) => ({ ...normalizeBankAccount(row), primary: rowIndex === index ? rawValue === true : false })) }));
      return;
    }
    let value = rawValue;
    if (["abi", "cab"].includes(key)) value = String(rawValue || "").replace(/\D/g, "").slice(0, 5);
    if (key === "accountNumber") value = String(rawValue || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12);
    if (key === "iban") value = normalizeIban(rawValue).slice(0, 27);
    if (key === "bic") value = String(rawValue || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 11);
    if (key === "siaCode") value = String(rawValue || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5);

    const currentRow = normalizeBankAccount(draft.bankAccount?.[index] || EMPTY_BANK_ACCOUNT);
    let next = { ...currentRow, [key]: value };
    let lookup = null;

    if (["iban", "abi", "cab"].includes(key)) {
      setBankLookupErrors((current) => ({ ...current, [index]: "" }));
      next = { ...next, bankName: "", branchName: "", address: "", cap: "", city: "", province: "" };
    }

    if (key === "accountNumber") {
      const calculated = calculateItalianIban(next.abi, next.cab, next.accountNumber);
      next = { ...next, iban: calculated?.iban || "", cin: calculated?.cin || "" };
    }
    if (key === "iban") {
      const parsed = parseItalianIban(value);
      if (parsed) {
        next = { ...next, ...parsed };
        lookup = { abi: parsed.abi, cab: parsed.cab };
      }
    } else if (["abi", "cab"].includes(key)) {
      if (next.abi.length === 5 && next.cab.length === 5) lookup = { abi: next.abi, cab: next.cab };
    }

    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      rows[index] = next;
      return { ...current, bankAccount: rows };
    });

    clearValidationKey(`bankAccount.${index}.${key}`);
    if (["abi", "cab", "accountNumber"].includes(key)) clearValidationKey(`bankAccount.${index}.iban`);
    setError("");
    if (lookup) void resolveBankAccount(index, lookup.abi, lookup.cab);
  }

  function commitBankAccountNumber(index) {
    if (!canEdit) return;
    const currentRow = normalizeBankAccount(draft.bankAccount?.[index] || EMPTY_BANK_ACCOUNT);
    const calculated = calculateItalianIban(currentRow.abi, currentRow.cab, currentRow.accountNumber);
    if (!calculated) return;
    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      rows[index] = { ...normalizeBankAccount(rows[index] || EMPTY_BANK_ACCOUNT), ...calculated };
      return { ...current, bankAccount: rows };
    });
    clearValidationKey(`bankAccount.${index}.iban`);
  }

  function bankMunicipalityOptions(bank = {}) {
    const current = findMunicipality(municipalities, bank.city, bank.province) || findMunicipality(municipalities, bank.city);
    const options = (municipalities || []).slice().sort((a, b) => String(a.description || "").localeCompare(String(b.description || ""), "it") || String(a.provinceCode || "").localeCompare(String(b.provinceCode || ""), "it")).map((item) => ({
      value: String(item.code || `${item.description}|${item.provinceCode || ""}`),
      label: item.provinceCode ? `${item.description} (${item.provinceCode})` : item.description,
    }));
    const value = String(current?.code || (bank.city ? `${bank.city}|${bank.province || ""}` : ""));
    if (value && !options.some((option) => option.value === value)) options.push({ value, label: bank.province ? `${bank.city} (${bank.province})` : bank.city });
    return { value, options, municipality: current };
  }

  function updateBankMunicipality(index, selectedValue) {
    if (!canEdit) return;
    const municipality = (municipalities || []).find((item) => String(item?.code || "") === String(selectedValue || "")) || (() => {
      const [description, provinceCode] = String(selectedValue || "").split("|");
      return findMunicipality(municipalities, description, provinceCode);
    })();
    const firstPostalCode = postalCodeCatalogOptions(postalCodes, municipality?.code || "")[0]?.value || "";
    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      rows[index] = { ...normalizeBankAccount(rows[index] || EMPTY_BANK_ACCOUNT), city: municipality?.description || "", province: municipality?.provinceCode || "", cap: normalizePostalCodeValue(firstPostalCode) };
      return { ...current, bankAccount: rows };
    });
  }

  function updateBankPostalCode(index, value) {
    if (!canEdit) return;
    setDraft((current) => {
      const rows = [...(current.bankAccount || [])];
      rows[index] = { ...normalizeBankAccount(rows[index] || EMPTY_BANK_ACCOUNT), cap: normalizePostalCodeValue(value) };
      return { ...current, bankAccount: rows };
    });
  }

  function companyFieldClass(key, extra = "") {
    return ["company_field", extra, validationActive && validationErrors[key] ? "is_invalid" : ""].filter(Boolean).join(" ");
  }

  function logoPositionFromPointer(event) {
    const drag = logoDragRef.current;
    const image = logoImageRef.current;
    if (!drag || !image) return logoPosition;

    const rect = event.currentTarget.getBoundingClientRect();
    const metrics = logoDrawMetrics(image, rect.width, rect.height, drag.zoom);
    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    const next = {
      x: metrics.overflowX > 0
        ? clampLogoPosition(drag.startPosition.x - (deltaX / metrics.overflowX) * 100)
        : 50,
      y: metrics.overflowY > 0
        ? clampLogoPosition(drag.startPosition.y - (deltaY / metrics.overflowY) * 100)
        : 50,
    };
    drag.lastPosition = next;
    return next;
  }

  async function persistLogoView(finalPosition, finalZoom, fallbackView = null) {
    if (!canEdit || !logoImageRef.current) return;

    const normalizedPosition = {
      x: clampLogoPosition(finalPosition?.x),
      y: clampLogoPosition(finalPosition?.y),
    };
    const normalizedZoom = clampLogoZoom(finalZoom);
    const fallback = fallbackView || savedLogoViewRef.current;

    // Se un salvataggio e gia in corso non blocchiamo pan/zoom: conserviamo
    // semplicemente l'ultima inquadratura e la inviamo appena il precedente termina.
    if (logoSaveInFlightRef.current) {
      logoPendingSaveRef.current = {
        position: normalizedPosition,
        zoom: normalizedZoom,
        fallbackView: fallback,
      };
      return;
    }

    const requestedView = { position: normalizedPosition, zoom: normalizedZoom };
    logoViewRef.current = requestedView;
    logoSaveInFlightRef.current = true;
    setSaving(true);
    setError("");
    const previousTenant = readStoredTenant();

    try {
      const cropBlob = await createLogoCropBlob(logoImageRef.current, normalizedPosition, normalizedZoom);
      const localLogoUrl = await logoBlobToDataUrl(cropBlob);
      if (previousTenant) syncTenant({ ...previousTenant, logoUrl: localLogoUrl, logoPath: localLogoUrl });

      const data = await uploadCompanyLogoCrop(cropBlob, {
        x: normalizedPosition.x,
        y: normalizedPosition.y,
        zoom: normalizedZoom,
      });
      const item = normalizeItem(data?.item);
      const currentView = logoViewRef.current;
      const hasNewerLocalView =
        Math.abs(clampLogoPosition(currentView.position?.x) - normalizedPosition.x) > 0.001 ||
        Math.abs(clampLogoPosition(currentView.position?.y) - normalizedPosition.y) > 0.001 ||
        Math.abs(clampLogoZoom(currentView.zoom) - normalizedZoom) > 0.0005;

      setSaved(item);
      setDraft((current) => ({
        ...current,
        logoUrl: item.logoUrl,
        logoSource: item.logoSource,
        logoPositionX: item.logoPositionX,
        logoPositionY: item.logoPositionY,
        logoZoom: item.logoZoom,
      }));
      setLogoPreview(item.logoUrl || "");

      // Non facciamo "saltare indietro" l'immagine se l'utente ha continuato
      // a usare la rotellina mentre la richiesta precedente era in volo.
      if (!hasNewerLocalView) {
        const savedPosition = {
          x: clampLogoPosition(item.logoPositionX),
          y: clampLogoPosition(item.logoPositionY),
        };
        const savedZoom = clampLogoZoom(item.logoZoom ?? normalizedZoom);
        logoViewRef.current = { position: savedPosition, zoom: savedZoom };
        setLogoPosition(savedPosition);
        setLogoZoom(savedZoom);
      }
      syncTenant(data?.tenant);
    } catch (err) {
      if (previousTenant) syncTenant(previousTenant);
      setError(apiError(err, "Impossibile aggiornare l'inquadratura del logo"));

      // Se nel frattempo l'utente ha gia creato una nuova inquadratura, la
      // manteniamo a video e lasciamo che venga salvata al giro successivo.
      const currentView = logoViewRef.current;
      const hasNewerLocalView =
        Math.abs(clampLogoPosition(currentView.position?.x) - requestedView.position.x) > 0.001 ||
        Math.abs(clampLogoPosition(currentView.position?.y) - requestedView.position.y) > 0.001 ||
        Math.abs(clampLogoZoom(currentView.zoom) - requestedView.zoom) > 0.0005;

      if (!hasNewerLocalView) {
        const fallbackPosition = {
          x: clampLogoPosition(fallback.position?.x),
          y: clampLogoPosition(fallback.position?.y),
        };
        const fallbackZoom = clampLogoZoom(fallback.zoom);
        logoViewRef.current = { position: fallbackPosition, zoom: fallbackZoom };
        setLogoPosition(fallbackPosition);
        setLogoZoom(fallbackZoom);
      }
    } finally {
      logoSaveInFlightRef.current = false;
      setSaving(false);

      const pending = logoPendingSaveRef.current;
      logoPendingSaveRef.current = null;
      if (pending) {
        // Avvia il salvataggio dell'ultima vista richiesta senza bloccare la UI.
        void persistLogoView(pending.position, pending.zoom, pending.fallbackView);
      }
    }
  }

  function startLogoDrag(event) {
    if (!canEdit || !logoImageRef.current) return;
    if (logoWheelTimerRef.current) {
      clearTimeout(logoWheelTimerRef.current);
      logoWheelTimerRef.current = null;
    }

    const currentView = logoViewRef.current;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    logoDragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startPosition: currentView.position,
      lastPosition: currentView.position,
      zoom: currentView.zoom,
    };
    setLogoDragging(true);
  }

  function moveLogoDrag(event) {
    if (!logoDragRef.current || !logoDragging) return;
    const nextPosition = logoPositionFromPointer(event);
    logoViewRef.current = { position: nextPosition, zoom: logoDragRef.current.zoom };
    setLogoPosition(nextPosition);
  }

  async function finishLogoDrag(event) {
    const drag = logoDragRef.current;
    if (!drag) return;

    const finalPosition = logoPositionFromPointer(event);
    logoDragRef.current = null;
    setLogoDragging(false);
    logoViewRef.current = { position: finalPosition, zoom: drag.zoom };
    setLogoPosition(finalPosition);

    if (!canEdit || !logoImageRef.current) return;
    await persistLogoView(finalPosition, drag.zoom, savedLogoViewRef.current);
  }

  function handleLogoWheel(event) {
    // preventDefault viene eseguito anche dal listener capture esterno, ma lo
    // ripetiamo qui per rendere l'handler sicuro anche se riutilizzato altrove.
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    if (!canEdit || !logoImageRef.current) return;

    const image = logoImageRef.current;
    const target = logoCanvasRef.current || logoPreviewBoxRef.current;
    if (!target) return;
    const rect = target.getBoundingClientRect();
    const currentView = logoViewRef.current;
    const currentPosition = currentView.position;
    const currentZoom = clampLogoZoom(currentView.zoom);
    const nextZoom = clampLogoZoom(currentZoom * Math.exp(-event.deltaY * LOGO_ZOOM_WHEEL_FACTOR));

    if (Math.abs(nextZoom - currentZoom) < 0.0005) return;

    const cursorX = Math.min(Math.max(event.clientX - rect.left, 0), rect.width);
    const cursorY = Math.min(Math.max(event.clientY - rect.top, 0), rect.height);
    const before = logoDrawMetrics(image, rect.width, rect.height, currentZoom);
    const after = logoDrawMetrics(image, rect.width, rect.height, nextZoom);
    const beforeDrawX = -before.overflowX * (clampLogoPosition(currentPosition.x) / 100);
    const beforeDrawY = -before.overflowY * (clampLogoPosition(currentPosition.y) / 100);
    const imagePointX = before.drawWidth > 0 ? (cursorX - beforeDrawX) / before.drawWidth : 0.5;
    const imagePointY = before.drawHeight > 0 ? (cursorY - beforeDrawY) / before.drawHeight : 0.5;

    const nextPosition = {
      x: after.overflowX > 0
        ? clampLogoPosition(((imagePointX * after.drawWidth) - cursorX) / after.overflowX * 100)
        : 50,
      y: after.overflowY > 0
        ? clampLogoPosition(((imagePointY * after.drawHeight) - cursorY) / after.overflowY * 100)
        : 50,
    };

    logoViewRef.current = { position: nextPosition, zoom: nextZoom };
    setLogoZoom(nextZoom);
    setLogoPosition(nextPosition);

    if (logoWheelTimerRef.current) clearTimeout(logoWheelTimerRef.current);
    logoWheelTimerRef.current = setTimeout(() => {
      logoWheelTimerRef.current = null;
      persistLogoView(nextPosition, nextZoom, savedLogoViewRef.current);
    }, LOGO_WHEEL_SAVE_DELAY);
  }

  async function chooseLogo(event) {
    const file = event.target.files?.[0] || null;
    if (!file || !canEdit || saving) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) {
      setError("Formato logo non supportato. Usa PNG, JPG/JPEG o WEBP");
      event.target.value = "";
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError("Il logo non può superare 2 MB");
      event.target.value = "";
      return;
    }

    setSaving(true);
    setError("");
    try {
      const data = await uploadCompanyLogo(file);
      const uploadedItem = normalizeItem(data?.item);
      const source = uploadedItem.logoSource || uploadedItem.logoUrl;
      const image = await loadLogoImage(source);
      const initialPosition = { x: 50, y: 50 };
      const initialZoom = 1;
      const cropBlob = await createLogoCropBlob(image, initialPosition, initialZoom);
      const croppedData = await uploadCompanyLogoCrop(cropBlob, { ...initialPosition, zoom: initialZoom });
      const item = normalizeItem(croppedData?.item);
      setSaved(item);
      setDraft((current) => ({
        ...current,
        logoUrl: item.logoUrl,
        logoSource: item.logoSource,
        logoPositionX: item.logoPositionX,
        logoPositionY: item.logoPositionY,
        logoZoom: item.logoZoom,
      }));
      logoImageRef.current = image;
      setLogoPreview(item.logoUrl || "");
      setLogoPosition({ x: clampLogoPosition(item.logoPositionX), y: clampLogoPosition(item.logoPositionY) });
      setLogoZoom(clampLogoZoom(item.logoZoom ?? initialZoom));
      syncTenant(croppedData?.tenant);
    } catch (err) {
      setError(apiError(err, "Impossibile caricare il logo"));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSaving(false);
    }
  }

  async function saveProfile() {
    if (!canEdit || saving) return;
    setError("");
    setValidationActive(true);
    const nextValidationErrors = validateCompanyDraft(draft);
    const firstLookupError = Object.entries(bankLookupErrors).find(([, message]) => String(message || "").trim());
    if (firstLookupError) {
      const index = Number(firstLookupError[0]);
      nextValidationErrors[`bankAccount.${index}.cab`] = String(firstLookupError[1]);
    }
    if (Object.keys(nextValidationErrors).length) {
      setValidationErrors(nextValidationErrors);
      setError("Dati Errati e/o Incompleti");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        subdomain: draft.subdomain,
        description: draft.description,
        legalForm: draft.legalForm,
        vatNumber: draft.vatNumber,
        fiscalCode: draft.fiscalCode,
        sdiCode: draft.sdiCode,
        reaCode: draft.reaCode,
        businessRegistryNumber: draft.businessRegistryNumber,
        shareCapital: draft.shareCapital,
        shareHoldersNumber: draft.shareHoldersNumber,
        liquidationStatus: draft.liquidationStatus,
        taxRegime: draft.taxRegime,
        eoriCode: draft.eoriCode,
        bankAccount: (draft.bankAccount || []).map(normalizeBankAccount),
      };
      const data = await updateCompanyProfile(payload);
      const item = normalizeItem(data?.item);
      setSaved(item);
      setDraft(item);
      setValidationActive(false);
      setValidationErrors({});
      setBankLookupErrors({});
      setLogoPreview(item.logoUrl || "");
      setLogoPosition({ x: clampLogoPosition(item.logoPositionX), y: clampLogoPosition(item.logoPositionY) });
      setLogoZoom(clampLogoZoom(item.logoZoom));
      syncTenant(data?.tenant);
      setError("");
    } catch (err) {
      const field = String(err?.response?.data?.field || "");
      const message = apiError(err, "Impossibile salvare l'anagrafica azienda");
      if (field) {
        setValidationErrors((current) => ({ ...current, [field]: message }));
        setValidationActive(true);
        setError("Dati Errati e/o Incompleti");
      } else {
        setError(message);
      }
    } finally {
      setSaving(false);
    }
  }

  async function removeLogo() {
    if (!canDelete || saving) return;
    setSaving(true);
    setError("");
    try {
      const data = await deleteCompanyLogo();
      const item = normalizeItem(data?.item);
      setSaved(item);
      setDraft((current) => ({
        ...current,
        logoUrl: "",
        logoSource: "",
        logoPositionX: 50,
        logoPositionY: 50,
        logoZoom: 1,
      }));
      setLogoPreview("");
      setLogoPosition({ x: 50, y: 50 });
      setLogoZoom(1);
      syncTenant(data?.tenant);
    } catch (err) {
      setError(apiError(err, "Impossibile eliminare il logo"));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <section className="window_page bookings_page management_page management_shared_page company_page">
        <div className="window_banner">
          <div className="window_banner_copy">
            <h1>AZIENDA</h1>
            <p>Anagrafica societaria, dati fiscali, logo e riferimenti bancari dell'hotel.</p>
          </div>
        </div>
        <div className="company_form_window">
          <div className="company_loading" role="status" aria-live="polite"><span className="company_loading_spinner" aria-hidden="true"><span /></span><span>Caricamento anagrafica azienda...</span></div>
        </div>
      </section>
    );
  }

  return (
    <section className="window_page bookings_page management_page management_shared_page company_page">
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>AZIENDA</h1>
          <p>Anagrafica societaria, dati fiscali, logo e riferimenti bancari dell'hotel.</p>
        </div>
      </div>

      <div className="company_form_window">
        <div className="company_form_body">
          <section className="company_section company_section_full company_logo_section">
            <div className="company_section_heading company_section_heading_compact">
              <div><h3>Logo Aziendale</h3></div>
            </div>
            <div className="company_logo_layout">
              <div ref={logoPreviewBoxRef} className={`company_logo_preview${logoPreview ? " has_image" : ""}${logoDragging ? " is_dragging" : ""}`}>
                {logoPreview ? (
                  <canvas
                    ref={logoCanvasRef}
                    className="company_logo_crop_canvas"
                    width={LOGO_CANVAS_WIDTH}
                    height={LOGO_CANVAS_HEIGHT}
                    aria-label="Posiziona e ingrandisci il logo aziendale"
                    onPointerDown={startLogoDrag}
                    onPointerMove={moveLogoDrag}
                    onPointerUp={finishLogoDrag}
                    onPointerCancel={finishLogoDrag}
                  />
                ) : <span>Nessun logo caricato</span>}
              </div>
              <div className="company_logo_content">
                <ul className="company_logo_instructions">
                  <li><strong>Caricamento:</strong> premi “Carica Logo” e scegli l'immagine da usare come logo aziendale.</li>
                  <li><strong>Dove viene mostrato:</strong> Topbar, report, anteprime di stampa e documenti fiscali come Fatture e Ricevute.</li>
                  <li><strong>Formati accettati:</strong> PNG, JPG/JPEG oppure WEBP, massimo 2 MB.</li>
                  <li><strong>Dimensione consigliata:</strong> immagine orizzontale di circa 1600 × 1000 px o superiore, nitida e con sufficiente spazio attorno al marchio.</li>
                  <li><strong>Posizionamento:</strong> porta il puntatore sopra il rettangolo, tieni premuto e trascina con la manina per scegliere esattamente la parte dell'immagine da mostrare.</li>
                  <li><strong>Zoom:</strong> con il puntatore sopra il logo usa la rotellina del mouse per zoom in e zoom out. Lo zoom out si ferma al limite necessario per mantenere sempre pieno il rettangolo, senza scoprire aree bianche; durante la regolazione la pagina non scorre.</li>
                  <li><strong>Aggiornamento:</strong> al rilascio del trascinamento o al termine dello zoom l'inquadratura viene salvata e propagata a Topbar, report e documenti fiscali.</li>
                </ul>
                <div className="company_logo_actions">
                  <input ref={fileInputRef} className="company_logo_input" type="file" accept="image/png,image/jpeg,image/webp" onChange={chooseLogo} disabled={!canEdit || saving} />
                  <button tabIndex={-1} type="button" className="company_logo_action_button" onClick={() => fileInputRef.current?.click()} disabled={!canEdit || saving}>Carica Logo</button>
                  <button tabIndex={-1} type="button" className="company_logo_action_button" onClick={removeLogo} disabled={!canDelete || !saved.logoUrl || saving}>Elimina Logo</button>
                </div>
              </div>
            </div>
          </section>

          <section className="company_section company_section_main">
            <div className="company_section_heading">
              <div>
                <h3>Dati Principali e Fiscali</h3>
                <p>Dati identificativi utilizzati nell'intestazione e nei documenti fiscali.</p>
              </div>
            </div>
            <div className="company_form_grid company_form_grid_2">
              <label className={companyFieldClass("subdomain", "company_field_span_2")}><span>Sottodominio <span className="overlay_required_mark" aria-hidden="true">*</span></span><input value={draft.subdomain} onChange={(e) => updateField("subdomain", e.target.value.toLowerCase())} disabled={!canEdit} maxLength={63} />{validationActive && validationErrors.subdomain ? <small className="company_field_error">{validationErrors.subdomain}</small> : null}</label>
              <label className={companyFieldClass("description", "company_field_span_2")}><span>Ragione Sociale <span className="overlay_required_mark" aria-hidden="true">*</span></span><input value={draft.description} onChange={(e) => updateField("description", e.target.value)} disabled={!canEdit} maxLength={180} />{validationActive && validationErrors.description ? <small className="company_field_error">{validationErrors.description}</small> : null}</label>
              <label className={companyFieldClass("legalForm")}><span>Forma Giuridica</span><VestaSelect value={draft.legalForm || ""} options={LEGAL_FORM_OPTIONS} placeholder="Seleziona forma giuridica" onValueChange={(value) => updateField("legalForm", value)} disabled={!canEdit} ariaLabel="Forma Giuridica" /></label>
              <label className={companyFieldClass("vatNumber")}><span>Partita IVA</span><input value={draft.vatNumber} onChange={(e) => updateField("vatNumber", e.target.value.toUpperCase())} disabled={!canEdit} />{validationActive && validationErrors.vatNumber ? <small className="company_field_error">{validationErrors.vatNumber}</small> : null}</label>
              <label className={companyFieldClass("fiscalCode")}><span>Codice Fiscale</span><input value={draft.fiscalCode} onChange={(e) => updateField("fiscalCode", e.target.value.toUpperCase())} disabled={!canEdit} />{validationActive && validationErrors.fiscalCode ? <small className="company_field_error">{validationErrors.fiscalCode}</small> : null}</label>
              <label className={companyFieldClass("sdiCode")}><span>Codice Destinatario / SDI</span><input value={draft.sdiCode} onChange={(e) => updateField("sdiCode", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7))} disabled={!canEdit} />{validationActive && validationErrors.sdiCode ? <small className="company_field_error">{validationErrors.sdiCode}</small> : null}</label>
              <label className={companyFieldClass("taxRegime")}><span>Regime Fiscale</span><VestaSelect value={draft.taxRegime || ""} options={TAX_REGIME_OPTIONS} placeholder="Seleziona regime fiscale" onValueChange={(value) => updateField("taxRegime", value)} disabled={!canEdit} ariaLabel="Regime Fiscale" /></label>
              <label className={companyFieldClass("eoriCode", "company_field_span_2")}><span>Codice EORI</span><input value={draft.eoriCode} onChange={(e) => updateField("eoriCode", e.target.value.toUpperCase())} disabled={!canEdit} /></label>
            </div>
          </section>

          <section className="company_section company_section_corporate">
            <div className="company_section_heading">
              <div>
                <h3>Dati Societari</h3>
                <p>Registrazione impresa, capitale e stato societario.</p>
              </div>
            </div>
            <div className="company_form_grid company_form_grid_2">
              <label className={companyFieldClass("reaCode")}><span>Codice REA</span><input value={draft.reaCode} onChange={(e) => updateField("reaCode", e.target.value.toUpperCase())} disabled={!canEdit} /></label>
              <label className={companyFieldClass("businessRegistryNumber")}><span>N. Registro Imprese</span><input value={draft.businessRegistryNumber} onChange={(e) => updateField("businessRegistryNumber", e.target.value.toUpperCase())} disabled={!canEdit} /></label>
              <label className={companyFieldClass("shareCapital", "company_currency_field")}><span>Capitale Sociale</span><div className="overlay_money_input_wrap company_share_capital_input"><span className="overlay_money_symbol" aria-hidden="true">€</span><input type="number" min="0" step="0.01" value={draft.shareCapital} onChange={(e) => updateField("shareCapital", e.target.value)} disabled={!canEdit} /></div></label>
              <label className={companyFieldClass("shareHoldersNumber")}><span>Numero Soci</span><input type="number" min="0" step="1" value={draft.shareHoldersNumber} onChange={(e) => updateField("shareHoldersNumber", e.target.value.replace(/\D/g, ""))} disabled={!canEdit} />{validationActive && validationErrors.shareHoldersNumber ? <small className="company_field_error">{validationErrors.shareHoldersNumber}</small> : null}</label>
              <label className={companyFieldClass("liquidationStatus", "company_field_span_2")}><span>Stato Liquidazione</span><VestaSelect value={draft.liquidationStatus} options={LIQUIDATION_OPTIONS} onValueChange={(value) => updateField("liquidationStatus", value)} disabled={!canEdit} ariaLabel="Stato Liquidazione" /></label>
            </div>
          </section>

          <section className="company_section company_section_full company_bank_section">
            <div className="company_section_heading">
              <div><h3>Dati Bancari</h3></div>
            </div>
            <div className="overlay_list_editor company_bank_list_editor">
              <div className="overlay_list_toolbar company_bank_list_toolbar">
                <button tabIndex={-1} type="button" className="overlay_list_add_button" onClick={addBankAccount} disabled={!canEdit || saving} aria-label="Aggiungi conto bancario" title="Aggiungi conto bancario">
                  <svg viewBox="0 0 24 24" aria-hidden="true" className="overlay_action_icon"><path d="M12 5v14M5 12h14" /></svg>
                </button>
                <p>Aggiungi uno o più conti. Inserendo l'IBAN vengono ricavati ABI, CAB, CIN e c/C; inserendo ABI, CAB e c/C viene calcolato l'IBAN. I dati di banca e sportello vengono verificati nella collection <strong>sys_bank</strong>.</p>
              </div>

              {(draft.bankAccount || []).length === 0 ? (
                <div className="overlay_list_empty">Nessun conto bancario inserito.</div>
              ) : (
                <div className="overlay_list_rows company_bank_rows">
                  {(draft.bankAccount || []).map((bank, index) => {
                    const fieldKey = (name) => `bankAccount.${index}.${name}`;
                    const municipalityField = bankMunicipalityOptions(bank);
                    const postalOptions = postalCodeCatalogOptions(postalCodes, municipalityField.municipality?.code || "", bank.cap);
                    return (
                      <div className={`overlay_list_row company_bank_row${expandedBankRows[index] ? " is_expanded" : ""}`} key={bank._id || `company-bank-${index}`}>
                        <label className={companyFieldClass(fieldKey("abi"), "company_bank_slot_1")}>
                          <span>ABI - Banca <span className="overlay_required_mark" aria-hidden="true">*</span></span>
                          <VestaSelect value={bank.abi || ""} onValueChange={(value) => selectBankAbi(index, value)} disabled={!canEdit} ariaLabel="ABI - Banca">
                            <option value="">Seleziona ABI - Banca</option>
                            {bank.abi && !bankCatalog.some((item) => String(item.abi) === String(bank.abi)) ? <option value={bank.abi}>{bank.abi} - {bank.bankName || "Banca"}</option> : null}
                            {bankCatalog.map((item) => <option key={item.abi} value={item.abi}>{item.abi} - {item.bankName}</option>)}
                          </VestaSelect>
                          {validationActive && validationErrors[fieldKey("abi")] ? <small className="company_field_error">{validationErrors[fieldKey("abi")]}</small> : null}
                        </label>

                        <label className={companyFieldClass(fieldKey("cab"), "company_bank_slot_2")}>
                          <span>CAB - Sportello <span className="overlay_required_mark" aria-hidden="true">*</span></span>
                          <VestaSelect value={bank.cab || ""} onValueChange={(value) => selectBankCab(index, value)} disabled={!canEdit || !/^\d{5}$/.test(String(bank.abi || ""))} ariaLabel="CAB - Sportello">
                            <option value="">Seleziona CAB - Sportello</option>
                            {bank.cab && !(bankBranches[bank.abi] || []).some((item) => String(item.cab) === String(bank.cab)) ? <option value={bank.cab}>{bank.cab} - {[bank.branchName, bank.city].filter(Boolean).join(" - ") || "Sportello"}</option> : null}
                            {(bankBranches[bank.abi] || []).map((item) => <option key={`${item.abi}-${item.cab}`} value={item.cab}>{item.cab} - {[item.branchName, item.city].filter(Boolean).join(" - ") || "Sportello"}</option>)}
                          </VestaSelect>
                          {validationActive && validationErrors[fieldKey("cab")] ? <small className="company_field_error">{validationErrors[fieldKey("cab")]}</small> : null}
                        </label>

                        <label className={companyFieldClass(fieldKey("accountNumber"), "company_bank_slot_3 company_bank_account")}>
                          <span>Conto Corrente - c/C <span className="overlay_required_mark" aria-hidden="true">*</span></span>
                          <input value={bank.accountNumber || ""} onChange={(e) => updateBankField(index, "accountNumber", e.target.value)} onBlur={() => commitBankAccountNumber(index)} disabled={!canEdit} maxLength={12} />
                          {validationActive && validationErrors[fieldKey("accountNumber")] ? <small className="company_field_error">{validationErrors[fieldKey("accountNumber")]}</small> : null}
                        </label>

                        <label className={companyFieldClass(fieldKey("iban"), "company_bank_slot_4 company_bank_iban")}>
                          <span>IBAN <span className="overlay_required_mark" aria-hidden="true">*</span></span>
                          <input value={bank.iban || ""} onChange={(e) => updateBankField(index, "iban", e.target.value)} disabled={!canEdit} maxLength={27} />
                          {validationActive && validationErrors[fieldKey("iban")] ? <small className="company_field_error">{validationErrors[fieldKey("iban")]}</small> : null}
                        </label>

                        <label className={companyFieldClass(fieldKey("cin"), "company_bank_slot_5")}><span>CIN</span><input value={bank.cin || ""} readOnly disabled /></label>
                        <label className="company_field company_bank_slot_6"><span>Località Succursale</span><VestaSelect value={municipalityField.value} options={municipalityField.options} placeholder="Seleziona Località" onValueChange={(value) => updateBankMunicipality(index, value)} disabled={!canEdit} ariaLabel="Località Succursale" /></label>
                        <label className="company_field company_bank_slot_7"><span>CAP Succursale</span><VestaSelect value={normalizePostalCodeValue(bank.cap)} options={postalOptions} placeholder="Seleziona CAP" onValueChange={(value) => updateBankPostalCode(index, value)} disabled={!canEdit || !bank.city} ariaLabel="CAP Succursale" /></label>
                        <label className="company_field company_bank_readonly company_bank_slot_8 company_bank_address"><span>Indirizzo Succursale</span><input value={bank.address || ""} readOnly disabled /></label>
                        <label className={companyFieldClass(fieldKey("siaCode"), "company_bank_slot_9 company_bank_extra_field")}><span>Codice SIA</span><input value={bank.siaCode || ""} onChange={(e) => updateBankField(index, "siaCode", e.target.value)} disabled={!canEdit} maxLength={5} />{validationActive && validationErrors[fieldKey("siaCode")] ? <small className="company_field_error">{validationErrors[fieldKey("siaCode")]}</small> : null}</label>
                        <label className={companyFieldClass(fieldKey("bic"), "company_bank_slot_10 company_bank_extra_field")}><span>BIC / SWIFT</span><input value={bank.bic || ""} onChange={(e) => updateBankField(index, "bic", e.target.value)} disabled={!canEdit} maxLength={11} />{validationActive && validationErrors[fieldKey("bic")] ? <small className="company_field_error">{validationErrors[fieldKey("bic")]}</small> : null}</label>
                        <label className={companyFieldClass(fieldKey("accountType"), "company_bank_slot_11 company_bank_extra_field")}><span>Tipo Rapporto</span><VestaSelect value={bank.accountType || "CURRENT"} options={BANK_ACCOUNT_TYPE_OPTIONS} onValueChange={(value) => updateBankField(index, "accountType", value)} disabled={!canEdit} ariaLabel="Tipo rapporto bancario" /></label>
                        <label className="overlay_field overlay_check_field customer_list_check_field company_bank_primary_check company_bank_slot_12 company_bank_extra_field"><span>Principale</span><input type="checkbox" checked={bank.primary === true} onChange={(e) => updateBankField(index, "primary", e.target.checked)} disabled={!canEdit} /></label>

                        <button tabIndex={-1} type="button" className="overlay_contact_remove_button overlay_list_remove_button company_bank_remove_button" onClick={() => removeBankAccount(index)} disabled={!canEdit || saving} aria-label={`Elimina conto ${index + 1}`} title="Elimina conto">×</button>
                        <button
                          tabIndex={-1}
                          type="button"
                          className="overlay_list_group_toggle vesta_filters_toggle company_bank_group_toggle"
                          onClick={() => setExpandedBankRows((current) => ({ ...current, [index]: !current[index] }))}
                          aria-expanded={Boolean(expandedBankRows[index])}
                          aria-label={expandedBankRows[index] ? "Nascondi valori aggiuntivi conto" : "Espandi valori conto"}
                          title={expandedBankRows[index] ? "Nascondi valori aggiuntivi" : "Espandi gruppo valori"}
                        >
                          <span className="overlay_list_group_toggle_icon vesta_filters_toggle_icon" aria-hidden="true"><span /><span /></span>
                        </button>
                        {bankLookupErrors[index] ? <div className="company_bank_lookup_error">{bankLookupErrors[index]}</div> : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="company_form_footer">
          <div className="company_feedback_slot">
            <div className="overlay_error_message">{error || (validationActive && Object.keys(validationErrors).length ? "Dati Errati e/o Incompleti" : "")}</div>
          </div>
          <div className="company_footer_actions">
            <button tabIndex={-1} type="button" className="company_footer_button" onClick={resetProfile} disabled={!canEdit || !hasProfileChanges || saving}>Azzera Modifiche</button>
            <button tabIndex={-1} type="button" className="company_footer_button" onClick={saveProfile} disabled={!canEdit || !hasProfileChanges || saving}>{saving ? "Salvataggio..." : "Salva"}</button>
          </div>
        </div>
      </div>
    </section>
  );
}
